import { clsx } from '../../../../ds/clsx';
import styles from './billing-ui.module.css';

/**
 * Splits a price label of the billing API into the amount and its unit: „79 € / Monat" → „79 €" + „/ Monat",
 * „79 € netto / Monat" → „79 €" + „netto / Monat", „kostenfrei" → „kostenfrei" + "".
 */
export function priceParts(label: string): { amount: string; unit: string } {
  const match = /^(.*?€)\s+(.+)$/.exec(label.trim());
  return match ? { amount: match[1] ?? label, unit: match[2] ?? '' } : { amount: label, unit: '' };
}

export interface PlanCardProps {
  /** „Makler · Einzelplatz", „Team · 8 Arbeitsplätze" */
  name: string;
  /** Features (E19), the note „monatlich kündbar" (Nutzer 10) or the seat line (E28). */
  detail?: string | null | undefined;
  /** A price label of the API: „79 € / Monat", „79 € netto / Monat", „237,00 € / Monat", „kostenfrei". */
  priceLabel: string;
  /**
   * `tariff`: the accent box of Einstellungen 19/28; `compact`: the plan line of Onboarding Nutzer 10;
   * `mobile`: the tariff card of Einstellungen M9 („Ihr Tarif", name, price line).
   */
  variant?: 'tariff' | 'compact' | 'mobile' | undefined;
  /** Mobile variant: text after the price, e.g. „nächste Abbuchung 01.10.2026". */
  suffix?: string | null | undefined;
  className?: string | undefined;
}

/** The plan of a billing page: name, detail line and the monthly price. */
export function PlanCard({ name, detail, priceLabel, variant = 'tariff', suffix, className }: PlanCardProps) {
  if (variant === 'mobile') {
    return (
      <section className={clsx(styles.planMobile, className)} aria-label="Ihr Tarif">
        <span className={styles.planMobileEyebrow}>Ihr Tarif</span>
        <span className={styles.planMobileName}>{name}</span>
        <span className={styles.planMobileLine}>{suffix ? `${priceLabel} · ${suffix}` : priceLabel}</span>
      </section>
    );
  }
  const { amount, unit } = priceParts(priceLabel);
  return (
    <div className={clsx(styles.plan, variant === 'compact' && styles.planCompact, className)}>
      <div className={styles.planText}>
        <span className={styles.planName}>{name}</span>
        {detail ? <span className={styles.planDetail}>{detail}</span> : null}
      </div>
      <span className={styles.planPrice}>
        {amount}
        {unit ? <span className={styles.planUnit}> {unit}</span> : null}
      </span>
    </div>
  );
}
